import moment from 'moment'
import { fetchCompany } from '@/api/parameters'
import { moneda } from '@/utils/filtros'

export async function generateMainDashboardPdf(payload) {
  const pdfMake = require("pdfmake/build/pdfmake.js")
  const pdfFonts = require("pdfmake/build/vfs_fonts.js")
  pdfMake.vfs = pdfFonts.pdfMake.vfs

  // 1. Datos de la empresa y parámetros
  let company = payload.company || {}
  try {
    if (!company.name && !company.fantasy) {
      company = await fetchCompany()
    }
  } catch (e) {
    company = {}
  }

  const parameters = payload.parameters || company || {}
  const countryCode = company.country_code || 'cl'
  const companyName = company.fantasy || company.name || 'Runflow ERP'
  const companyRut = company.rut || ''
  const timezone = payload.serverTimezone || company.current_time_zone || 'America/Santiago'

  const formatMoney = (val) => moneda(Number(val || 0), countryCode)

  const startDateFormatted = payload.startDate ? moment(payload.startDate).format('DD/MM/YYYY') : '—'
  const endDateFormatted = payload.endDate ? moment(payload.endDate).format('DD/MM/YYYY') : '—'
  const periodLabel = `${startDateFormatted} al ${endDateFormatted}`

  const now = moment()
  const emissionDate = now.format('DD/MM/YYYY HH:mm')

  const metrics = payload.metrics || {}
  const relbaseMetrics = payload.relbaseMetrics || {}
  const relbaseEnabled = !!parameters.relbase_integration_enabled
  const topCustomers = payload.topCustomers || []
  const topSellers = payload.topSellers || []

  // 2. Determinar tarjetas métricas activas según parámetros
  const allCards = [
    {
      id: 'quoted',
      active: parameters.dashboard_card_quoted !== false,
      title: 'COTIZADO TOTAL',
      value: formatMoney(metrics.total_quoted),
      sub: 'Proyecciones y cotizaciones registradas',
      color: '#0284C7'
    },
    {
      id: 'confirmed',
      active: parameters.dashboard_card_confirmed !== false,
      title: 'VENTAS CONFIRMADAS',
      value: formatMoney(metrics.total_confirmed),
      sub: 'Órdenes de trabajo confirmadas',
      color: '#00A650'
    },
    {
      id: 'confirmed_finished',
      active: parameters.dashboard_card_confirmed_finished !== false,
      title: 'PRODUCCIÓN FINALIZADA',
      value: formatMoney(metrics.total_confirmed_finished),
      sub: 'OTs listas para despacho/entrega',
      color: '#7C3AED'
    },
    {
      id: 'confirmed_finished_not_invoiced',
      active: parameters.dashboard_card_confirmed_finished_not_invoiced === true,
      title: 'PENDIENTE FACTURAR',
      value: formatMoney(metrics.total_confirmed_finished_not_invoiced),
      sub: 'Finalizado sin DTE emitido',
      color: '#EA580C'
    },
    {
      id: 'invoiced',
      active: parameters.dashboard_card_invoiced !== false,
      title: 'FACTURADO NETO',
      value: formatMoney(metrics.total_invoiced),
      sub: 'Ingresos reconocidos en el período',
      color: '#0D9488'
    },
    {
      id: 'paid',
      active: parameters.dashboard_card_paid !== false,
      title: 'RECAUDACIÓN REAL',
      value: formatMoney(metrics.total_payments),
      sub: 'Flujo de caja efectivo registrado',
      color: '#16A34A'
    },
    {
      id: 'purchase_subtotal',
      active: parameters.dashboard_card_purchase_subtotal !== false,
      title: 'COMPRAS NETO',
      value: formatMoney(metrics.total_purchase_subtotal),
      sub: 'Costos operativos y suministros',
      color: '#DC2626'
    },
    {
      id: 'purchase_paid',
      active: parameters.dashboard_card_purchase_paid !== false,
      title: 'COMPRAS PAGADO',
      value: formatMoney(metrics.total_purchase_payments),
      sub: 'Egresos consolidados pagados',
      color: '#475569'
    }
  ]

  const activeCards = allCards.filter(c => c.active)

  // Armar filas de la tabla de tarjetas (máximo 4 por fila)
  const cardsPerRow = activeCards.length <= 3 ? Math.max(1, activeCards.length) : (activeCards.length === 6 ? 3 : 4)
  const metricTableRows = []
  for (let i = 0; i < activeCards.length; i += cardsPerRow) {
    const chunk = activeCards.slice(i, i + cardsPerRow)
    const rowCells = chunk.map(card => ({
      fillColor: '#F8FAFC',
      border: [true, true, true, true],
      borderColor: ['#E2E8F0', '#E2E8F0', '#E2E8F0', '#E2E8F0'],
      margin: [0, 0, 0, 0],
      stack: [
        {
          text: card.title,
          fontSize: 6.5,
          bold: true,
          color: '#64748B',
          characterSpacing: 0.5,
          margin: [10, 8, 10, 2]
        },
        {
          text: card.value,
          fontSize: 13,
          bold: true,
          color: '#0F172A',
          margin: [10, 0, 10, 2]
        },
        {
          text: card.sub,
          fontSize: 6.5,
          color: '#94A3B8',
          margin: [10, 0, 10, 8]
        }
      ]
    }))

    // Rellenar celdas vacías si la última fila tiene menos tarjetas que el ancho
    while (rowCells.length < cardsPerRow) {
      rowCells.push({
        text: '',
        border: [false, false, false, false],
        fillColor: '#FFFFFF'
      })
    }
    metricTableRows.push(rowCells)
  }

  const cardWidths = Array(cardsPerRow).fill('*')

  // 3. Tablas de Ranking (Top Clientes y Top Vendedores)
  const customerRows = [
    [
      { text: '#', style: 'th', alignment: 'center', width: 20 },
      { text: 'CLIENTE', style: 'th', width: '*' },
      { text: 'CONFIRMADO', style: 'th', alignment: 'right', width: 75 },
      { text: 'FACTURADO', style: 'th', alignment: 'right', width: 75 }
    ]
  ]

  if (topCustomers.length === 0) {
    customerRows.push([
      { text: '—', colSpan: 4, alignment: 'center', color: '#94A3B8', fontSize: 8, margin: [0, 8, 0, 8] },
      {}, {}, {}
    ])
  } else {
    topCustomers.slice(0, 5).forEach((c, idx) => {
      const isEven = idx % 2 === 0
      customerRows.push([
        { text: String(idx + 1).padStart(2, '0'), fontSize: 7.5, bold: true, color: '#64748B', alignment: 'center', fillColor: isEven ? '#F8FAFC' : '#FFFFFF' },
        { text: c.name || 'Sin nombre', fontSize: 8, bold: true, color: '#1E293B', fillColor: isEven ? '#F8FAFC' : '#FFFFFF' },
        { text: formatMoney(c.confirmed), fontSize: 8, bold: true, color: '#00A650', alignment: 'right', fillColor: isEven ? '#F8FAFC' : '#FFFFFF' },
        { text: formatMoney(c.invoiced), fontSize: 8, color: '#64748B', alignment: 'right', fillColor: isEven ? '#F8FAFC' : '#FFFFFF' }
      ])
    })
  }

  const sellerRows = [
    [
      { text: '#', style: 'th', alignment: 'center', width: 20 },
      { text: 'EJECUTIVO / VENDEDOR', style: 'th', width: '*' },
      { text: 'VENTA CONFIRMADA', style: 'th', alignment: 'right', width: 85 },
      { text: 'RECAUDO / FACT.', style: 'th', alignment: 'right', width: 75 }
    ]
  ]

  if (topSellers.length === 0) {
    sellerRows.push([
      { text: '—', colSpan: 4, alignment: 'center', color: '#94A3B8', fontSize: 8, margin: [0, 8, 0, 8] },
      {}, {}, {}
    ])
  } else {
    topSellers.slice(0, 5).forEach((s, idx) => {
      const isEven = idx % 2 === 0
      sellerRows.push([
        { text: String(idx + 1).padStart(2, '0'), fontSize: 7.5, bold: true, color: '#64748B', alignment: 'center', fillColor: isEven ? '#F8FAFC' : '#FFFFFF' },
        { text: s.name || 'Sin nombre', fontSize: 8, bold: true, color: '#1E293B', fillColor: isEven ? '#F8FAFC' : '#FFFFFF' },
        { text: formatMoney(s.confirmed), fontSize: 8, bold: true, color: '#0284C7', alignment: 'right', fillColor: isEven ? '#F8FAFC' : '#FFFFFF' },
        { text: formatMoney(s.invoiced), fontSize: 8, color: '#64748B', alignment: 'right', fillColor: isEven ? '#F8FAFC' : '#FFFFFF' }
      ])
    })
  }

  // 4. Document Definition
  const dd = {
    pageSize: 'LETTER',
    pageOrientation: 'landscape',
    pageMargins: [28, 22, 28, 22],
    footer: function(currentPage, pageCount) {
      return {
        margin: [28, 0, 28, 0],
        columns: [
          { text: 'RUNFLOW ERP · DASHBOARD PRINCIPAL DE GESTIÓN OPERATIVA Y FINANCIERA', fontSize: 7, color: '#94A3B8', bold: true },
          { text: `Página ${currentPage} de ${pageCount}`, alignment: 'right', fontSize: 7, color: '#94A3B8', bold: true }
        ]
      }
    },
    content: [
      // Banner Cabecera Midnight Slate (#0F172A)
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                fillColor: '#0F172A',
                border: [false, false, false, false],
                margin: [16, 12, 16, 12],
                columns: [
                  {
                    width: '*',
                    stack: [
                      {
                        text: 'RUNFLOW · SNAPSHOT EJECUTIVO DASHBOARD PRINCIPAL',
                        fontSize: 8,
                        bold: true,
                        color: '#00A650',
                        characterSpacing: 1.5,
                        margin: [0, 0, 0, 3]
                      },
                      {
                        text: `${companyName}`,
                        fontSize: 16,
                        bold: true,
                        color: '#FFFFFF',
                        margin: [0, 0, 0, 3]
                      },
                      {
                        text: `${companyRut ? 'RUT: ' + companyRut + '  ·  ' : ''}Rango Evaluado: ${periodLabel}`,
                        fontSize: 9,
                        color: '#CBD5E1'
                      }
                    ]
                  },
                  {
                    width: 220,
                    alignment: 'right',
                    stack: [
                      {
                        text: 'CONFIDENCIAL · REPORTE EJECUTIVO',
                        fontSize: 7.5,
                        bold: true,
                        color: '#FCA5A5',
                        margin: [0, 0, 0, 6]
                      },
                      {
                        text: `Emisión: ${emissionDate} hrs`,
                        fontSize: 8,
                        color: '#94A3B8',
                        margin: [0, 0, 0, 2]
                      },
                      {
                        text: `Zona Horaria: ${timezone}`,
                        fontSize: 7.5,
                        color: '#64748B'
                      }
                    ]
                  }
                ]
              }
            ]
          ]
        },
        margin: [0, 0, 0, 10],
        layout: {
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0
        }
      },

      // Tarjetas Métricas Dinámicas (según configuración de parámetros)
      ...(metricTableRows.length > 0 ? [
        {
          table: {
            widths: cardWidths,
            body: metricTableRows
          },
          margin: [0, 0, 0, 10],
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#E2E8F0',
            vLineColor: () => '#E2E8F0',
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0
          }
        }
      ] : []),

      // Bloque Opcional de Facturación Relbase (si está habilitado)
      ...(relbaseEnabled ? [
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  fillColor: '#F0FDF4',
                  border: [true, true, true, true],
                  borderColor: ['#BBF7D0', '#BBF7D0', '#BBF7D0', '#BBF7D0'],
                  margin: [12, 6, 12, 6],
                  columns: [
                    {
                      width: 170,
                      stack: [
                        { text: 'SINCRONIZACIÓN RELBASE', fontSize: 7, bold: true, color: '#15803D', characterSpacing: 1 },
                        { text: 'Estado Facturación Electrónica', fontSize: 8.5, bold: true, color: '#0F172A', margin: [0, 1, 0, 0] }
                      ]
                    },
                    {
                      width: '*',
                      columns: [
                        {
                          stack: [
                            { text: 'TOTAL FACTURADO', fontSize: 6.5, bold: true, color: '#64748B' },
                            { text: formatMoney(relbaseMetrics.totalFacturado), fontSize: 10, bold: true, color: '#0F172A' }
                          ]
                        },
                        {
                          stack: [
                            { text: 'DTES RECIENTES', fontSize: 6.5, bold: true, color: '#64748B' },
                            { text: `${relbaseMetrics.dtesRecientes || 0} docs`, fontSize: 10, bold: true, color: '#0F172A' }
                          ]
                        },
                        {
                          stack: [
                            { text: 'PENDIENTES FACTURAR', fontSize: 6.5, bold: true, color: '#64748B' },
                            { text: `${relbaseMetrics.pendientesFacturacion || 0} OTs`, fontSize: 10, bold: true, color: '#EA580C' }
                          ]
                        },
                        {
                          stack: [
                            { text: 'MONTO PENDIENTE', fontSize: 6.5, bold: true, color: '#64748B' },
                            { text: formatMoney(relbaseMetrics.montosPendientesFacturacion), fontSize: 10, bold: true, color: '#EA580C' }
                          ]
                        }
                      ]
                    }
                  ]
                }
              ]
            ]
          },
          margin: [0, 0, 0, 10],
          layout: {
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0
          }
        }
      ] : []),

      // Sección de Rankings Comerciales en 2 Columnas (Top 5 Clientes y Top 5 Vendedores)
      {
        columns: [
          // Columna 1: Top Clientes
          {
            width: '*',
            stack: [
              {
                text: 'TOP 5 CLIENTES POR MONTO CONFIRMADO',
                fontSize: 8,
                bold: true,
                color: '#0F172A',
                characterSpacing: 0.5,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: [20, '*', 75, 75],
                  body: customerRows
                },
                layout: {
                  hLineWidth: (i) => i === 1 ? 1 : 0.5,
                  vLineWidth: () => 0,
                  hLineColor: (i) => i === 1 ? '#0F172A' : '#E2E8F0',
                  paddingLeft: () => 4,
                  paddingRight: () => 4,
                  paddingTop: () => 3,
                  paddingBottom: () => 3
                }
              }
            ]
          },
          { width: 14, text: '' },
          // Columna 2: Top Vendedores
          {
            width: '*',
            stack: [
              {
                text: 'TOP 5 VENDEDORES POR VENTA CONFIRMADA',
                fontSize: 8,
                bold: true,
                color: '#0F172A',
                characterSpacing: 0.5,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: [20, '*', 85, 75],
                  body: sellerRows
                },
                layout: {
                  hLineWidth: (i) => i === 1 ? 1 : 0.5,
                  vLineWidth: () => 0,
                  hLineColor: (i) => i === 1 ? '#0F172A' : '#E2E8F0',
                  paddingLeft: () => 4,
                  paddingRight: () => 4,
                  paddingTop: () => 3,
                  paddingBottom: () => 3
                }
              }
            ]
          }
        ]
      }
    ],
    styles: {
      th: {
        fontSize: 7,
        bold: true,
        color: '#FFFFFF',
        fillColor: '#0F172A',
        margin: [0, 2, 0, 2]
      }
    },
    defaultStyle: {
      font: 'Roboto'
    }
  }

  pdfMake.createPdf(dd).download(`Dashboard_Principal_${moment().format('YYYYMMDD_HHmm')}.pdf`)
}
